import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth.jsx';
import PhoneInput from '../components/PhoneInput.jsx';
import CaptchaWidget from '../components/CaptchaWidget.jsx';
import { DEFAULT_COUNTRY, findCountry } from '../lib/countries.js';

const SELF_REGISTERABLE = [
  { value: 'student', label: 'I\'m a student', sub: 'Take the UCAT, apply to med/dental school' },
  { value: 'parent', label: 'I\'m a parent', sub: 'Track your child\'s progress' },
  { value: 'teacher', label: 'I\'m a teacher', sub: 'Help your students prep for the UCAT' },
];

const card = {
  background: '#fff',
  borderRadius: 18,
  padding: '32px 36px',
  boxShadow: '0 18px 48px rgba(20, 5, 60, 0.08)',
  border: '1px solid #ece8f6',
  maxWidth: 580,
  margin: '0 auto',
};
const label = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#3f3a5e' };
const input = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 10,
  border: '1px solid #d8d6e6',
  fontSize: 14.5,
  outline: 'none',
  background: '#fff',
  boxSizing: 'border-box',
};
const fieldGap = { marginBottom: 16 };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };
const sectionHead = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: '#5b21b6',
  margin: '22px 0 12px',
};
const errorBox = {
  background: '#fee2e2',
  color: '#991b1b',
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: 13.5,
  marginBottom: 14,
};
const submitBtn = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: 10,
  border: 'none',
  background: 'linear-gradient(135deg, #5b21b6, #7c3aed)',
  color: '#fff',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  marginTop: 18,
};

export default function Signup() {
  const { register, user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = SELF_REGISTERABLE.find((r) => r.value === params.get('role'))?.value || 'student';

  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState(DEFAULT_COUNTRY.code);

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactCountry, setContactCountry] = useState(DEFAULT_COUNTRY.code);

  const [termsAccepted, setTermsAccepted] = useState(false);
  // GDPR/PECR: marketing consent must be an active opt-in — unticked by default.
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === 'admin' || user.role === 'superadmin' ? '/admin' : '/', { replace: true });
    }
  }, [user, loading, navigate]);

  const needsCounterpart = role === 'student' || role === 'parent';
  const counterpartLabel = role === 'student' ? 'parent / guardian' : 'student';

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!termsAccepted) {
      setErr('Please agree to the Terms and Conditions to continue.');
      return;
    }
    if (import.meta.env.VITE_TURNSTILE_SITE_KEY && !captchaToken) {
      setErr('Please complete the captcha.');
      return;
    }
    setSubmitting(true);
    try {
      const dialCode = findCountry(phoneCountry)?.dial || '';
      const e164Phone = dialCode
        ? `${dialCode}${phone.replace(/\s+/g, '').replace(/^0+/, '')}`
        : phone;
      const contactDialCode = findCountry(contactCountry)?.dial || '';
      const e164ContactPhone = contactDialCode && contactPhone
        ? `${contactDialCode}${contactPhone.replace(/\s+/g, '').replace(/^0+/, '')}`
        : contactPhone;
      const payload = {
        email,
        password,
        name,
        role,
        phone: e164Phone,
        phoneCountry,
        country: phoneCountry,
        termsAccepted: true,
        marketingConsent,
        captchaToken,
      };
      if (needsCounterpart) {
        payload.relatedContact = {
          name: contactName,
          email: contactEmail,
          phone: e164ContactPhone,
          phoneCountry: contactCountry,
        };
      }
      await register(payload);
      navigate('/verify-email?fresh=1', { replace: true });
    } catch (e2) {
      setErr(e2.message || 'Sign-up failed. Please check your details.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mm-auth-page" style={{ minHeight: '100vh', background: '#f7f5fc', padding: '24px 16px 48px' }}>
      <div style={{ maxWidth: 580, margin: '0 auto 22px' }}>
        <Link to="/" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10, color: '#1f1240' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #5b21b6, #7c3aed)',
              color: '#fff',
              fontWeight: 800,
              fontSize: 15,
            }}
          >
            MM
          </span>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            MEDI · <span style={{ color: '#6b7280', fontWeight: 500 }}>powered by Medic Mind</span>
          </span>
        </Link>
      </div>

      <div className="mm-auth-card" style={card}>
        <div className="mm-auth-kicker" style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
          Create your free account
        </div>
        <h1 style={{ margin: '0 0 6px', fontSize: 26 }}>Join Medic Mind</h1>
        <p style={{ margin: '0 0 22px', color: '#6b7280', fontSize: 14.5, lineHeight: 1.5 }}>
          Track drills, save progress, and unlock the full skills trainer.
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#5b21b6', fontWeight: 600 }}>
            Sign in →
          </Link>
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 22 }}>
          {SELF_REGISTERABLE.map((r) => {
            const active = role === r.value;
            return (
              <button
                key={r.value}
                type="button"
                onClick={() => setRole(r.value)}
                style={{
                  padding: '12px 10px',
                  borderRadius: 12,
                  border: active ? '2px solid #5b21b6' : '1px solid #e5e3f0',
                  background: active ? '#f3eefc' : '#fff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  boxShadow: active ? '0 6px 16px rgba(91, 33, 182, 0.12)' : 'none',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 14, color: active ? '#5b21b6' : '#1f1240' }}>
                  {r.label}
                </div>
                <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2, lineHeight: 1.4 }}>
                  {r.sub}
                </div>
              </button>
            );
          })}
        </div>

        {err && <div className="mm-auth-error" style={errorBox}>{err}</div>}

        <form onSubmit={submit} noValidate>
          <div style={sectionHead}>Your details</div>

          <div style={fieldGap}>
            <label style={label}>Full name</label>
            <input
              style={input}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
              placeholder="Jane Smith"
            />
          </div>

          <div style={{ ...grid2, ...fieldGap }}>
            <div>
              <label style={label}>Email</label>
              <input
                style={input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label style={label}>Password</label>
              <input
                style={input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                placeholder="8+ chars, 1 number"
              />
            </div>
          </div>

          <div style={fieldGap}>
            <label style={label}>Phone number (required)</label>
            <PhoneInput
              value={phone}
              onChange={setPhone}
              countryCode={phoneCountry}
              onCountryChange={setPhoneCountry}
              required
            />
          </div>

          {needsCounterpart && (
            <>
              <div style={sectionHead}>{counterpartLabel} contact</div>
              <p style={{ margin: '-6px 0 12px', fontSize: 13, color: '#6b7280' }}>
                We'll use this to keep your {counterpartLabel} in the loop on important
                admissions milestones.
              </p>

              <div style={fieldGap}>
                <label style={label}>{counterpartLabel} full name</label>
                <input
                  style={input}
                  type="text"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  required
                  placeholder="Full name"
                />
              </div>

              <div style={{ ...grid2, ...fieldGap }}>
                <div>
                  <label style={label}>{counterpartLabel} email</label>
                  <input
                    style={input}
                    type="email"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    required
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label style={label}>{counterpartLabel} phone <span style={{ color: '#9ca3af', fontWeight: 500 }}>(optional)</span></label>
                  <PhoneInput
                    value={contactPhone}
                    onChange={setContactPhone}
                    countryCode={contactCountry}
                    onCountryChange={setContactCountry}
                  />
                </div>
              </div>
            </>
          )}

          <div style={sectionHead}>Consent</div>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, marginBottom: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              required
              style={{ marginTop: 3 }}
            />
            <span>
              I agree to the{' '}
              <a href="/terms" target="_blank" rel="noreferrer" style={{ color: '#5b21b6', fontWeight: 600 }}>
                Terms and Conditions
              </a>{' '}
              and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: '#5b21b6', fontWeight: 600 }}>
                Privacy Policy
              </a>
              .
            </span>
          </label>

          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, marginBottom: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              <strong>Yes, keep me in the loop (optional).</strong> Email me UCAT tips,
              mock-exam reminders, and Career Camp offers from Medic Mind. We'll never
              share your details, and you can unsubscribe in one click from any email.
            </span>
          </label>
          <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 18px', lineHeight: 1.5 }}>
            Leaving this unticked is completely fine — you'll still get essential account
            emails (verification, password resets and security notices), just no marketing.
          </p>

          <CaptchaWidget onToken={setCaptchaToken} />

          <button type="submit" className="mm-auth-primary" style={submitBtn} disabled={submitting}>
            {submitting ? 'Creating account…' : 'Create my Medic Mind account'}
          </button>
        </form>
      </div>
    </div>
  );
}
